import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import NotAuthorized from '../../components/admin/NotAuthorized';
import DestructiveActionConfirm from '../../components/admin/DestructiveActionConfirm';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminSection from '../../components/admin/ui/AdminSection';
import AdminAlert from '../../components/admin/ui/AdminAlert';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';
import { normalizeApiError, userMessageForError } from '../../utils/adminApiError';
import AdminTable, { AdminTableHeader, AdminTableRow, AdminTableCell, AdminTableHeaderCell } from '../../components/admin/ui/AdminTable';
import {
  getKillSwitchStatus,
  enableKillSwitch,
  disableKillSwitch,
  getSystemHealth,
  getSystemReadiness,
  getAdminMe,
  getRecentCriticalEvents,
} from '../../services/opsService';
import { createIntent } from '../../services/intentService';
import { triggerSync } from '../../services/adminIntegrationSyncService';
import { getIntegrations } from '../../services/integrationService';
import { getSubscriptions, upsertSubscription, updateSubscription } from '../../services/adminNotificationsService';

export default function OpsPage() {
  const navigate = useNavigate();
  const { has } = useCapabilities();
  
  // Capability checks
  const canReadIntegrations = has('admin:integrations:read');
  const canReadAudit = has('admin:audit:read');
  const canReadOwnerDashboard = has('admin:owner-dashboard:read');
  const canManageIntegrations = has('admin:integrations:manage');
  
  // Minimum read capability required
  const canRead = canReadIntegrations || canReadAudit || canReadOwnerDashboard;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Kill Switch
  const [killSwitchStatus, setKillSwitchStatus] = useState(null);
  const [killSwitchLoading, setKillSwitchLoading] = useState(false);
  const [killSwitchError, setKillSwitchError] = useState(null);
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false);
  const [killSwitchAction, setKillSwitchAction] = useState(null);

  // System Health
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);
  
  const [readinessStatus, setReadinessStatus] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(null);
  
  const [adminMeStatus, setAdminMeStatus] = useState(null);
  const [adminMeLoading, setAdminMeLoading] = useState(false);
  const [adminMeError, setAdminMeError] = useState(null);

  // Critical Events
  const [criticalEvents, setCriticalEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  // Quick Actions
  const [syncIntegrationCode, setSyncIntegrationCode] = useState('');
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showSyncConfirmRow, setShowSyncConfirmRow] = useState({ open: false, code: null });
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Integrations Needing Attention
  const [integrationsNeedingAttention, setIntegrationsNeedingAttention] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState(null);
  const [healthFilter, setHealthFilter] = useState(null); // 'ERROR', 'WARN', 'recent-failures'
  const [syncLoadingStates, setSyncLoadingStates] = useState({});
  const [syncErrors, setSyncErrors] = useState({});

  useEffect(() => {
    if (canRead) {
      loadAll();
    } else {
      setLoading(false);
    }
  }, [canRead]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    
    await Promise.all([
      loadKillSwitchStatus(),
      loadSystemHealth(),
      loadCriticalEvents(),
      loadIntegrationsNeedingAttention(),
    ]);
    
    setLoading(false);
  };

  const loadKillSwitchStatus = async () => {
    try {
      const response = await getKillSwitchStatus();
      setKillSwitchStatus({
        disabled: response.globally_disabled || false,
        updatedAt: response.updated_at || null,
        updatedBy: response.updated_by || null,
        reason: response.reason || null,
      });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setKillSwitchError(userMessageForError(normalized));
    }
  };

  const loadSystemHealth = async () => {
    setHealthLoading(true);
    setReadinessLoading(true);
    setAdminMeLoading(true);
    
    try {
      const [health, readiness, adminMe] = await Promise.all([
        getSystemHealth().catch(err => ({ error: err.message })),
        getSystemReadiness().catch(err => ({ error: err.message })),
        getAdminMe().catch(err => ({ error: err.message })),
      ]);
      
      setHealthStatus(health);
      setReadinessStatus(readiness);
      setAdminMeStatus(adminMe);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setHealthError(userMessageForError(normalized));
    } finally {
      setHealthLoading(false);
      setReadinessLoading(false);
      setAdminMeLoading(false);
    }
  };

  const loadCriticalEvents = async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const response = await getRecentCriticalEvents(20);
      setCriticalEvents(response.items || []);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setEventsError(userMessageForError(normalized));
    } finally {
      setEventsLoading(false);
    }
  };

  const loadIntegrationsNeedingAttention = async () => {
    try {
      setIntegrationsLoading(true);
      setIntegrationsError(null);
      const response = await getIntegrations();
      const allIntegrations = response.integrations || [];
      // Filter for integrations with health.level !== 'OK'
      const needingAttention = allIntegrations.filter(
        (int) => int.health && int.health.level && int.health.level !== 'OK'
      );
      // Sort by health level (ERROR first, then WARN)
      needingAttention.sort((a, b) => {
        if (a.health.level === 'ERROR' && b.health.level !== 'ERROR') return -1;
        if (a.health.level !== 'ERROR' && b.health.level === 'ERROR') return 1;
        return 0;
      });
      // Limit to top 10
      setIntegrationsNeedingAttention(needingAttention.slice(0, 10));
    } catch (err) {
      const normalized = normalizeApiError(err);
      setIntegrationsError(userMessageForError(normalized));
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleKillSwitchToggle = (action) => {
    if (!canManageIntegrations) return;
    setKillSwitchAction(action);
    setShowKillSwitchConfirm(true);
  };

  const handleKillSwitchConfirm = async (reason) => {
    try {
      setKillSwitchLoading(true);
      setKillSwitchError(null);

      const intentAction = killSwitchAction === 'enable'
        ? 'global_kill_switch.enable'
        : 'global_kill_switch.disable';
      
      const intentResponse = await createIntent({
        action: intentAction,
        targetType: 'system',
        targetId: 'global_kill_switch',
        reason: reason.trim(),
        risk_acknowledged: true,
      });

      if (killSwitchAction === 'enable') {
        await enableKillSwitch(reason, intentResponse.intentAuditEventId);
      } else {
        await disableKillSwitch(reason, intentResponse.intentAuditEventId);
      }

      setShowKillSwitchConfirm(false);
      setKillSwitchAction(null);
      await loadKillSwitchStatus();
    } catch (err) {
      setKillSwitchError(err.message || `Failed to ${killSwitchAction} kill switch`);
    } finally {
      setKillSwitchLoading(false);
    }
  };

  const handleSyncTrigger = () => {
    if (!canManageIntegrations || !syncIntegrationCode.trim()) return;
    setShowSyncConfirm(true);
  };

  const handleSyncConfirm = async (reason) => {
    try {
      setSyncLoading(true);
      setSyncError(null);

      const intentResponse = await createIntent({
        action: 'integration.sync.requested',
        targetType: 'integration',
        targetId: syncIntegrationCode.trim(),
        reason: reason.trim(),
        risk_acknowledged: true,
      });

      await triggerSync(syncIntegrationCode.trim(), reason, intentResponse.intentAuditEventId);
      
      setShowSyncConfirm(false);
      setSyncIntegrationCode('');
      await loadCriticalEvents(); // Refresh events to show new sync
    } catch (err) {
      setSyncError(err.message || 'Failed to trigger sync');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleEventClick = (event) => {
    // Navigate to audit page with prefilled filters
    const params = new URLSearchParams();
    if (event.action) params.append('action', event.action);
    if (event.actor_id) params.append('actor_id', event.actor_id);
    if (event.target_id) params.append('target_id', event.target_id);
    if (event.request_id) params.append('request_id', event.request_id);
    if (event.result) params.append('outcome', event.result);
    
    navigate(`/admin/audit?${params.toString()}`);
  };

  if (!canRead) {
    return (
      <NotAuthorized 
        requiredCapability="admin:integrations:read OR admin:audit:read OR admin:owner-dashboard:read" 
      />
    );
  }

  if (loading) {
    return <div style={styles.container}>Loading operations status...</div>;
  }

  // Get mutation freeze and egress status from environment (read-only)
  // These would come from a backend endpoint in production
  const mutationFreezeEnabled = false; // Would come from ADMIN_MUTATIONS_ENABLED env var
  const egressEnabled = true; // Would come from INTEGRATIONS_EGRESS_ENABLED env var

  return (
    <div className="admin-surface">
      <AdminPageHeader
        title="Operations & Safeguards"
        subtitle="System health monitoring and operational controls"
      />

      {error && (
        <AdminAlert
          type="error"
          message={
            <>
              {error}
              <AdminButton
                variant="secondary"
                onClick={loadAll}
                style={{ marginLeft: '0.5rem' }}
              >
                Retry
              </AdminButton>
            </>
          }
        />
      )}

      {/* Safeguards Panel */}
      <AdminSection title="Safeguards">
        {/* Global Kill Switch */}
        <AdminCard style={{ marginBottom: 'var(--spacing-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
            <h3 className="admin-section-title">Global Kill Switch</h3>
            <AdminStatusBadge variant={killSwitchStatus?.disabled ? 'error' : 'success'}>
              {killSwitchStatus?.disabled ? 'DISABLED' : 'ENABLED'}
            </AdminStatusBadge>
          </div>
          {killSwitchError && (
            <AdminAlert type="error" message={killSwitchError} style={{ marginBottom: 'var(--spacing-4)' }} />
          )}
          {killSwitchStatus && (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <p className="admin-text-secondary">Status: {killSwitchStatus.disabled ? 'All integrations disabled globally' : 'Integrations enabled'}</p>
              {killSwitchStatus.updatedAt && (
                <p className="admin-text-secondary">Last updated: {new Date(killSwitchStatus.updatedAt).toLocaleString()}</p>
              )}
              {killSwitchStatus.updatedBy && (
                <p className="admin-text-secondary">Updated by: {killSwitchStatus.updatedBy}</p>
              )}
              {killSwitchStatus.reason && (
                <p className="admin-text-secondary">Reason: {killSwitchStatus.reason}</p>
              )}
            </div>
          )}
          {canManageIntegrations && (
            <AdminButton
              variant={killSwitchStatus?.disabled ? 'primary' : 'destructive'}
              onClick={() => handleKillSwitchToggle(killSwitchStatus?.disabled ? 'enable' : 'disable')}
              disabled={killSwitchLoading}
              loading={killSwitchLoading}
              ariaLabel={killSwitchStatus?.disabled ? 'Enable integrations' : 'Disable all integrations'}
            >
              {killSwitchStatus?.disabled ? 'Enable Integrations' : 'Disable All Integrations'}
            </AdminButton>
          )}
        </AdminCard>

        {/* Mutation Freeze */}
        <AdminCard style={{ marginBottom: 'var(--spacing-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
            <h3 className="admin-section-title">Mutation Freeze</h3>
            <AdminStatusBadge variant={mutationFreezeEnabled ? 'error' : 'success'}>
              {mutationFreezeEnabled ? 'FROZEN' : 'ACTIVE'}
            </AdminStatusBadge>
          </div>
          <div>
            <p className="admin-text-secondary">Status: {mutationFreezeEnabled ? 'All admin mutations are frozen' : 'Admin mutations are allowed'}</p>
            <p className="admin-text-tertiary admin-text-sm" style={{ marginTop: 'var(--spacing-2)' }}>
              <em>Note: Controlled by ADMIN_MUTATIONS_ENABLED environment variable. Read-only in UI.</em>
            </p>
          </div>
        </AdminCard>

        {/* Integration Egress */}
        <AdminCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
            <h3 className="admin-section-title">Integration Egress</h3>
            <AdminStatusBadge variant={egressEnabled ? 'success' : 'error'}>
              {egressEnabled ? 'ALLOWED' : 'BLOCKED'}
            </AdminStatusBadge>
          </div>
          <div>
            <p className="admin-text-secondary">Status: {egressEnabled ? 'Outbound integration calls are allowed' : 'All outbound integration calls are blocked'}</p>
            <p className="admin-text-tertiary admin-text-sm" style={{ marginTop: 'var(--spacing-2)' }}>
              <em>Note: Controlled by INTEGRATIONS_EGRESS_ENABLED environment variable. Read-only in UI.</em>
            </p>
          </div>
        </AdminCard>
      </AdminSection>

      {/* System Health Panel */}
      <AdminSection title="System Health">
        <div className="admin-grid admin-grid-cols-3" style={{ gap: 'var(--spacing-4)' }}>
          {/* /api/health */}
          <AdminCard>
            <h4 className="admin-section-title">/api/health</h4>
            {healthLoading ? (
              <div className="admin-loading">Loading...</div>
            ) : healthError ? (
              <AdminAlert type="error" message={healthError} />
            ) : healthStatus?.error ? (
              <AdminAlert type="error" message={healthStatus.error} />
            ) : (
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <pre className="admin-code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(healthStatus, null, 2)}</pre>
              </div>
            )}
            <AdminButton
              variant="secondary"
              onClick={loadSystemHealth}
              ariaLabel="Refresh health status"
            >
              Refresh
            </AdminButton>
          </AdminCard>

          {/* /api/readyz */}
          <AdminCard>
            <h4 className="admin-section-title">/api/readyz</h4>
            {readinessLoading ? (
              <div className="admin-loading">Loading...</div>
            ) : readinessError ? (
              <AdminAlert type="error" message={readinessError} />
            ) : readinessStatus?.error ? (
              <AdminAlert type="error" message={readinessStatus.error} />
            ) : (
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <pre className="admin-code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(readinessStatus, null, 2)}</pre>
              </div>
            )}
            <AdminButton
              variant="secondary"
              onClick={loadSystemHealth}
              ariaLabel="Refresh readiness status"
            >
              Refresh
            </AdminButton>
          </AdminCard>

          {/* /api/v1/admin/me */}
          <AdminCard>
            <h4 className="admin-section-title">/api/v1/admin/me</h4>
            {adminMeLoading ? (
              <div className="admin-loading">Loading...</div>
            ) : adminMeError ? (
              <AdminAlert type="error" message={adminMeError} />
            ) : adminMeStatus?.error ? (
              <AdminAlert type="error" message={adminMeStatus.error} />
            ) : (
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <div style={{ marginBottom: 'var(--spacing-2)' }}>
                  <p className="admin-text-secondary"><strong className="admin-text-primary">Actor ID:</strong> {adminMeStatus?.actorId || adminMeStatus?.id || 'N/A'}</p>
                  <p className="admin-text-secondary"><strong className="admin-text-primary">Capabilities:</strong> {adminMeStatus?.capabilities?.length || 0} capabilities</p>
                </div>
                <details>
                  <summary className="admin-link" style={{ cursor: 'pointer', marginBottom: 'var(--spacing-2)' }}>Full Response</summary>
                  <pre className="admin-code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '300px', overflow: 'auto', marginTop: 'var(--spacing-2)' }}>{JSON.stringify(adminMeStatus, null, 2)}</pre>
                </details>
              </div>
            )}
            <AdminButton variant="secondary" onClick={loadSystemHealth}>
              Refresh
            </AdminButton>
          </AdminCard>
        </div>
      </AdminSection>

      {/* Integrations Needing Attention Panel */}
      {canReadIntegrations && (
        <AdminCard title="Integrations Needing Attention">
          {/* Quick Filters */}
          {integrationsNeedingAttention.length > 0 && (
            <div className="admin-toolbar" style={{ marginBottom: 'var(--spacing-4)' }}>
              <AdminButton
                variant={healthFilter === 'ERROR' ? 'primary' : 'secondary'}
                onClick={() => setHealthFilter(healthFilter === 'ERROR' ? null : 'ERROR')}
              >
                Health=ERROR
              </AdminButton>
              <AdminButton
                variant={healthFilter === 'WARN' ? 'primary' : 'secondary'}
                onClick={() => setHealthFilter(healthFilter === 'WARN' ? null : 'WARN')}
              >
                Health=WARN
              </AdminButton>
              <AdminButton
                variant={healthFilter === 'recent-failures' ? 'primary' : 'secondary'}
                onClick={() => setHealthFilter(healthFilter === 'recent-failures' ? null : 'recent-failures')}
              >
                Recent failures (24h)
              </AdminButton>
            </div>
          )}

          {integrationsLoading ? (
            <div className="admin-loading">Loading integrations...</div>
          ) : integrationsError ? (
            <AdminAlert type="error" message={integrationsError} />
          ) : filteredIntegrations.length === 0 ? (
            <div className="admin-text-tertiary admin-text-center" style={{ padding: 'var(--spacing-8)' }}>
              {healthFilter ? 'No integrations match the filter' : 'All integrations are healthy'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
              {filteredIntegrations.map((integration) => (
                <AdminCard key={integration.code} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/integrations/${integration.code}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-2)' }}>
                    <div>
                      <strong className="admin-text-primary">{integration.name || integration.code}</strong>
                      <span className="admin-code" style={{ marginLeft: 'var(--spacing-1)' }}> ({integration.code})</span>
                    </div>
                    <AdminStatusBadge variant={integration.health.level === 'WARN' ? 'warning' : 'error'}>
                      {integration.health.level}
                    </AdminStatusBadge>
                  </div>
                  {integration.health.reasons && integration.health.reasons.length > 0 && (
                    <div className="admin-text-secondary admin-text-sm" style={{ marginBottom: 'var(--spacing-2)' }}>
                      {integration.health.reasons[0]}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <AdminButton
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/integrations/${integration.code}`);
                      }}
                    >
                      View Detail
                    </AdminButton>
                    <AdminButton
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/audit?integration=${encodeURIComponent(integration.code)}`);
                      }}
                    >
                      View Audit
                    </AdminButton>
                    {canManageIntegrations && (
                      <AdminButton
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSyncConfirmRow({ open: true, code: integration.code });
                        }}
                        disabled={syncLoadingStates[integration.code]}
                      >
                        {syncLoadingStates[integration.code] ? 'Running...' : 'Run Sync'}
                      </AdminButton>
                    )}
                  </div>
                  {syncErrors[integration.code] && (
                    <AdminAlert type="error" message={syncErrors[integration.code]} style={{ marginTop: 'var(--spacing-2)' }} />
                  )}
                </AdminCard>
              ))}
            </div>
          )}
        </AdminCard>
      )}

      {/* Recent Critical Events Panel */}
      <AdminCard title="Recent Critical Events">
        {eventsLoading ? (
          <div className="admin-loading">Loading events...</div>
        ) : eventsError ? (
          <AdminAlert type="error" message={eventsError} />
        ) : criticalEvents.length === 0 ? (
          <div className="admin-text-tertiary admin-text-center" style={{ padding: 'var(--spacing-8)' }}>No critical events found</div>
        ) : (
          <>
            <AdminTable>
              <AdminTableHeader>
                <AdminTableHeaderCell>Time</AdminTableHeaderCell>
                <AdminTableHeaderCell>Action</AdminTableHeaderCell>
                <AdminTableHeaderCell>Outcome</AdminTableHeaderCell>
                <AdminTableHeaderCell>Actor</AdminTableHeaderCell>
                <AdminTableHeaderCell>Target</AdminTableHeaderCell>
              </AdminTableHeader>
              <tbody>
                {criticalEvents.map((event) => (
                  <AdminTableRow
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AdminTableCell>{new Date(event.ts).toLocaleString()}</AdminTableCell>
                    <AdminTableCell><code className="admin-code">{event.action}</code></AdminTableCell>
                    <AdminTableCell>
                      <AdminStatusBadge variant={event.result === 'failure' ? 'error' : 'success'}>
                        {event.result}
                      </AdminStatusBadge>
                    </AdminTableCell>
                    <AdminTableCell>{event.actor_id}</AdminTableCell>
                    <AdminTableCell>{event.target_type}:{event.target_id}</AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
            <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', justifyContent: 'flex-end' }}>
              <AdminButton variant="secondary" onClick={loadCriticalEvents}>
                Refresh
              </AdminButton>
            </div>
          </>
        )}
      </AdminCard>

      {/* Quick Actions Panel (Optional) */}
      {canManageIntegrations && (
        <AdminCard title="Quick Actions">
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end' }}>
            <div className="admin-form-group" style={{ flex: 1 }}>
              <label className="admin-form-label">
                Trigger Sync for Integration:
                <input
                  type="text"
                  value={syncIntegrationCode}
                  onChange={(e) => setSyncIntegrationCode(e.target.value)}
                  placeholder="e.g., hostaway"
                  className="admin-input"
                />
              </label>
            </div>
            <AdminButton
              variant="primary"
              onClick={handleSyncTrigger}
              disabled={!syncIntegrationCode.trim() || syncLoading}
            >
              Trigger Sync
            </AdminButton>
          </div>
          {syncError && (
            <AdminAlert type="error" message={syncError} style={{ marginTop: 'var(--spacing-4)' }} />
          )}
        </AdminCard>
      )}

      {/* Kill Switch Confirm Modal */}
      <DestructiveActionConfirm
        open={showKillSwitchConfirm}
        onClose={() => {
          if (!killSwitchLoading) {
            setShowKillSwitchConfirm(false);
            setKillSwitchAction(null);
          }
        }}
        onConfirm={handleKillSwitchConfirm}
        title={`${killSwitchAction === 'enable' ? 'Enable' : 'Disable'} Global Kill Switch`}
        description={`Are you sure you want to ${killSwitchAction === 'enable' ? 'enable' : 'disable'} all integrations globally? This will ${killSwitchAction === 'enable' ? 'allow' : 'block'} all integration operations.`}
        confirmLabel={killSwitchAction === 'enable' ? 'Enable' : 'Disable'}
        reasonLabel="Reason"
        reasonPlaceholder="e.g., 'Emergency maintenance', 'Security incident'"
        loading={killSwitchLoading}
        error={killSwitchError}
      />

      {/* Sync Confirm Modal (Quick Actions) */}
      <DestructiveActionConfirm
        open={showSyncConfirm && !showSyncConfirm.code}
        onClose={() => {
          if (!syncLoading) {
            setShowSyncConfirm(false);
          }
        }}
        onConfirm={handleSyncConfirm}
        title={`Trigger Sync: ${syncIntegrationCode}`}
        description={`Trigger a sync run for integration "${syncIntegrationCode}"?`}
        confirmLabel="Trigger Sync"
        reasonLabel="Reason"
        reasonPlaceholder="e.g., 'Manual sync for data refresh'"
        loading={syncLoading}
        error={syncError}
      />

      {/* Sync Confirm Modal (Integration Row) */}
      <DestructiveActionConfirm
        open={showSyncConfirmRow.open && !!showSyncConfirmRow.code}
        onClose={() => {
          if (!syncLoadingStates[showSyncConfirmRow.code]) {
            setShowSyncConfirmRow({ open: false, code: null });
          }
        }}
        onConfirm={(reason) => handleIntegrationSync(showSyncConfirmRow.code, reason)}
        title={`Trigger Sync: ${showSyncConfirmRow.code}`}
        description={`Trigger a sync run for integration "${showSyncConfirmRow.code}"?`}
        confirmLabel="Trigger Sync"
        reasonLabel="Reason"
        reasonPlaceholder="e.g., 'Manual sync for data refresh'"
        loading={syncLoadingStates[showSyncConfirmRow.code]}
        error={syncErrors[showSyncConfirmRow.code]}
      />
    </div>
  );
}

